import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, RefreshCw,
  Activity, Users, ArrowRight, Zap, Star,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface OrgData {
  org_id: string;
  org_name: string;
  status: string;
  scored_7d: number;
  scored_30d: number;
  wau_7d: number;
  fsr_lift_30d: number;
  pct_users_improving: number;
  active_users: number;
  total_users: number;
  last_activity: string | null;
}

interface HealthFlag {
  id: string;
  org_id: string;
  flag_type: string;
  severity: string;
  details: any;
  created_at: string;
}

type TrajectoryLabel = 'Improving' | 'Steady' | 'Stalling' | 'Dropping' | 'No data';

interface OrgPulse extends OrgData {
  // this week vs estimated prior week (scored_30d - scored_7d) / 3.3
  trajectoryRatio: number;
  trajectoryDelta: number;   // absolute change: scored_7d - priorWeekEst
  trajectoryLabel: TrajectoryLabel;
  activationPct: number;
  flagCount: number;
  highFlagCount: number;
}

// ── Helpers ───────────────────────────────────────────────────────

/**
 * Estimate prior-week activity from 30-day data.
 * prior_week_est = (scored_30d - scored_7d) / 3.3
 * (removes this week from the 30-day window, divides remaining ~23 days by 3.3 to get a weekly rate)
 */
function computeTrajectory(scored7d: number, scored30d: number): { ratio: number; delta: number } {
  const prior = Math.max(scored30d - scored7d, 0);
  const priorWeekEst = prior / 3.3;
  if (priorWeekEst < 1) {
    // New or near-zero org — can't derive meaningful trajectory
    return { ratio: scored7d > 0 ? 1.5 : 0, delta: scored7d };
  }
  return {
    ratio: scored7d / priorWeekEst,
    delta: Math.round(scored7d - priorWeekEst),
  };
}

function getTrajectoryLabel(ratio: number, scored30d: number): TrajectoryLabel {
  if (scored30d === 0) return 'No data';
  if (ratio >= 1.2)  return 'Improving';
  if (ratio >= 0.85) return 'Steady';
  if (ratio >= 0.5)  return 'Stalling';
  return 'Dropping';
}

function trajectoryMeta(label: TrajectoryLabel) {
  switch (label) {
    case 'Improving': return { color: 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30',   bar: 'bg-green-500',   icon: TrendingUp };
    case 'Steady':    return { color: 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30',       bar: 'bg-blue-400',    icon: Minus };
    case 'Stalling':  return { color: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30', bar: 'bg-yellow-500', icon: TrendingDown };
    case 'Dropping':  return { color: 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30',           bar: 'bg-red-500',     icon: TrendingDown };
    case 'No data':   return { color: 'border-muted text-muted-foreground', bar: 'bg-muted', icon: Minus };
  }
}

function severityColor(s: string) {
  if (s === 'high')   return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30';
  if (s === 'medium') return 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30';
  return 'bg-muted text-muted-foreground';
}

// ── Main component ────────────────────────────────────────────────

export default function PlatformPulse() {
  const navigate = useNavigate();

  const [pulseData, setPulseData]         = useState<OrgPulse[]>([]);
  const [flags, setFlags]                 = useState<HealthFlag[]>([]);
  const [flagOrgMap, setFlagOrgMap]       = useState<Record<string, string>>({});
  const [loading, setLoading]             = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const [sortField, setSortField] = useState<'trajectory' | 'activation' | 'fsr_lift' | 'flags' | 'activity'>('flags');
  const [sortDir, setSortDir]     = useState<'asc' | 'desc'>('desc');

  const load = async () => {
    setLoading(true);
    try {
      const [orgsRes, flagsRes] = await Promise.all([
        supabase.rpc('get_all_orgs_admin'),
        supabase
          .from('org_health_flags')
          .select('id, org_id, flag_type, severity, details, created_at')
          .is('resolved_at', null)
          .order('severity', { ascending: true })
          .order('created_at', { ascending: false }),
      ]);

      const orgs: OrgData[]        = orgsRes.data  || [];
      const allFlags: HealthFlag[] = (flagsRes.data || []) as unknown as HealthFlag[];

      // Flag counts per org
      const flagCountMap:     Record<string, number> = {};
      const highFlagCountMap: Record<string, number> = {};
      for (const f of allFlags) {
        flagCountMap[f.org_id]     = (flagCountMap[f.org_id]     ?? 0) + 1;
        if (f.severity === 'high') highFlagCountMap[f.org_id] = (highFlagCountMap[f.org_id] ?? 0) + 1;
      }

      // Org name map for flag display
      const orgNameMap: Record<string, string> = {};
      for (const o of orgs) orgNameMap[o.org_id] = o.org_name;
      setFlagOrgMap(orgNameMap);

      const cleanPulse: OrgPulse[] = orgs.map(o => {
        const { ratio, delta } = computeTrajectory(o.scored_7d, o.scored_30d);
        return {
          ...o,
          trajectoryRatio: ratio,
          trajectoryDelta: delta,
          trajectoryLabel: getTrajectoryLabel(ratio, o.scored_30d),
          activationPct:   o.total_users > 0 ? Math.round((o.active_users / o.total_users) * 100) : 0,
          flagCount:       flagCountMap[o.org_id]     ?? 0,
          highFlagCount:   highFlagCountMap[o.org_id] ?? 0,
        };
      });

      setPulseData(cleanPulse);
      setFlags(allFlags);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('PlatformPulse load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const sorted = [...pulseData].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    switch (sortField) {
      case 'trajectory': return (a.trajectoryRatio - b.trajectoryRatio) * dir;
      case 'activation': return (a.activationPct   - b.activationPct)   * dir;
      case 'fsr_lift':   return (a.fsr_lift_30d     - b.fsr_lift_30d)   * dir;
      case 'flags':      return (a.highFlagCount    - b.highFlagCount)   * dir;
      case 'activity':   return (a.scored_7d        - b.scored_7d)       * dir;
      default:           return 0;
    }
  });

  // Multiplier spotlight — match by name (case-insensitive)
  const multiplier = pulseData.find(o => o.org_name.toLowerCase().includes('multiplier'));
  const multiplierFlags = flags.filter(f => f.org_id === multiplier?.org_id);

  // Summary counts
  const improving  = pulseData.filter(o => o.trajectoryLabel === 'Improving').length;
  const stalling   = pulseData.filter(o => o.trajectoryLabel === 'Stalling').length;
  const dropping   = pulseData.filter(o => o.trajectoryLabel === 'Dropping').length;
  const highFlags  = flags.filter(f => f.severity === 'high').length;

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-64">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Activity className="h-5 w-5 animate-pulse" />
          <span className="text-sm">Loading platform data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Platform Pulse</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Engagement trajectory across all orgs — this week vs prior week
            {lastRefreshed && ` · refreshed ${formatDistanceToNow(lastRefreshed, { addSuffix: true })}`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-muted-foreground uppercase tracking-wide font-medium">Total Orgs</p>
            <p className="text-2xl font-bold mt-1">{pulseData.length}</p>
          </CardContent>
        </Card>
        <Card className="border-green-500/20">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-green-700 dark:text-green-400 uppercase tracking-wide font-medium">Improving</p>
            <p className="text-2xl font-bold mt-1 text-green-700 dark:text-green-400">{improving}</p>
          </CardContent>
        </Card>
        <Card className="border-yellow-500/20">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-yellow-700 dark:text-yellow-400 uppercase tracking-wide font-medium">Stalling</p>
            <p className="text-2xl font-bold mt-1 text-yellow-700 dark:text-yellow-400">{stalling + dropping}</p>
          </CardContent>
        </Card>
        <Card className="border-red-500/20">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-red-700 dark:text-red-400 uppercase tracking-wide font-medium">High Flags</p>
            <p className="text-2xl font-bold mt-1 text-red-700 dark:text-red-400">{highFlags}</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Multiplier spotlight ─────────────────────────────────────── */}
      {multiplier && (
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Star className="h-4 w-4 text-primary" />
              Multiplier — 30-Seat Rollout Activation
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
              {/* Activation progress */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Seats activated</p>
                <p className="text-2xl font-bold">
                  {multiplier.active_users}
                  <span className="text-sm font-normal text-muted-foreground"> / {multiplier.total_users}</span>
                </p>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden mt-2 w-full">
                  <div
                    className={`h-full rounded-full ${multiplier.activationPct >= 70 ? 'bg-green-500' : multiplier.activationPct >= 40 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${multiplier.activationPct}%` }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">{multiplier.activationPct}% activated</p>
              </div>

              {/* Trajectory */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Trajectory</p>
                {(() => {
                  const meta = trajectoryMeta(multiplier.trajectoryLabel);
                  const TIcon = meta.icon;
                  return (
                    <div className="flex items-center gap-1.5 mt-1">
                      <TIcon className="h-5 w-5" />
                      <span className="font-semibold">{multiplier.trajectoryLabel}</span>
                    </div>
                  );
                })()}
                <p className="text-xs text-muted-foreground mt-1">
                  {multiplier.trajectoryDelta >= 0 ? '+' : ''}{multiplier.trajectoryDelta} vs prior week
                </p>
              </div>

              {/* FSR lift */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">FSR lift (30d)</p>
                <p className={`text-2xl font-bold ${multiplier.fsr_lift_30d > 0 ? 'text-green-600 dark:text-green-400' : multiplier.fsr_lift_30d < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                  {multiplier.fsr_lift_30d > 0 ? '+' : ''}{multiplier.fsr_lift_30d.toFixed(1)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">AI fluency change</p>
              </div>

              {/* 7d activity */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">This week</p>
                <p className="text-2xl font-bold">{multiplier.scored_7d}</p>
                <p className="text-xs text-muted-foreground mt-1">prompts submitted</p>
              </div>
            </div>

            {/* Health flags for Multiplier */}
            {multiplierFlags.length > 0 && (
              <div className="border-t border-border/50 pt-3 space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Active Flags ({multiplierFlags.length})
                </p>
                {multiplierFlags.map(f => (
                  <div key={f.id} className="flex items-center gap-2 text-xs">
                    <Badge variant="outline" className={`text-xs capitalize shrink-0 ${severityColor(f.severity)}`}>
                      {f.severity}
                    </Badge>
                    <span className="text-muted-foreground">{f.flag_type.replace(/_/g, ' ')}</span>
                    <span className="text-muted-foreground/60 ml-auto">
                      {formatDistanceToNow(new Date(f.created_at), { addSuffix: true })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end mt-3">
              <Button size="sm" variant="outline" onClick={() => navigate(`/admin/org/${multiplier.org_id}`)}>
                Full org detail <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Velocity table ────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Zap className="h-4 w-4" />
            All Orgs — Engagement Trajectory
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Trajectory = this week's activity ÷ estimated prior-week rate (derived from 30-day window).
            &gt;1.2 = improving · 0.85–1.2 = steady · &lt;0.5 = dropping.
            Default sorted by risk (high flags first).
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide w-48">
                    Org
                  </th>
                  <th
                    className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('trajectory')}
                  >
                    Trajectory {sortField === 'trajectory' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th
                    className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('activity')}
                  >
                    7d / prior {sortField === 'activity' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th
                    className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('fsr_lift')}
                  >
                    FSR lift {sortField === 'fsr_lift' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th
                    className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('activation')}
                  >
                    Activation {sortField === 'activation' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th
                    className="text-right px-4 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide cursor-pointer hover:text-foreground"
                    onClick={() => toggleSort('flags')}
                  >
                    Flags {sortField === 'flags' ? (sortDir === 'desc' ? '↓' : '↑') : ''}
                  </th>
                  <th className="w-10" />
                </tr>
              </thead>
              <tbody>
                {sorted.map((org) => {
                  const meta  = trajectoryMeta(org.trajectoryLabel);
                  const TIcon = meta.icon;
                  const barPct = Math.min(100, Math.round(org.trajectoryRatio * 50));
                  const isMultiplier = org.org_name.toLowerCase().includes('multiplier');
                  return (
                    <tr
                      key={org.org_id}
                      className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${isMultiplier ? 'bg-primary/5' : ''}`}
                    >
                      {/* Org */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {isMultiplier && <Star className="h-3 w-3 text-primary shrink-0" />}
                          <p className="font-medium text-sm truncate">{org.org_name}</p>
                        </div>
                        <p className="text-xs text-muted-foreground capitalize">{org.status}</p>
                      </td>

                      {/* Trajectory */}
                      <td className="px-4 py-3 min-w-[180px]">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className={`text-xs ${meta.color}`}>
                            <TIcon className="h-3 w-3 mr-1" />
                            {org.trajectoryLabel}
                          </Badge>
                          {org.trajectoryLabel !== 'No data' && (
                            <span className="text-xs text-muted-foreground">
                              {org.trajectoryRatio.toFixed(1)}x
                            </span>
                          )}
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden w-32">
                          <div className={`h-full ${meta.bar} rounded-full transition-all`} style={{ width: `${barPct}%` }} />
                        </div>
                      </td>

                      {/* 7d / delta */}
                      <td className="px-4 py-3 text-right">
                        <p className="font-medium">{org.scored_7d}</p>
                        <p className={`text-xs ${org.trajectoryDelta >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {org.trajectoryDelta >= 0 ? '+' : ''}{org.trajectoryDelta} vs prior wk
                        </p>
                      </td>

                      {/* FSR lift */}
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium text-sm ${org.fsr_lift_30d > 0 ? 'text-green-600 dark:text-green-400' : org.fsr_lift_30d < 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>
                          {org.fsr_lift_30d > 0 ? '+' : ''}{org.fsr_lift_30d.toFixed(1)}
                        </span>
                      </td>

                      {/* Activation */}
                      <td className="px-4 py-3 text-right">
                        <p className={`font-medium text-sm ${org.activationPct < 40 ? 'text-yellow-600 dark:text-yellow-400' : org.activationPct >= 70 ? 'text-green-600 dark:text-green-400' : ''}`}>
                          {org.activationPct}%
                        </p>
                        <p className="text-xs text-muted-foreground">{org.active_users}/{org.total_users}</p>
                      </td>

                      {/* Flags */}
                      <td className="px-4 py-3 text-right">
                        {org.flagCount > 0 ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${
                            org.highFlagCount > 0
                              ? 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/30'
                              : 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30'
                          }`}>
                            <AlertTriangle className="h-3 w-3" />
                            {org.flagCount}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>

                      {/* Navigate */}
                      <td className="px-2 py-3">
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => navigate(`/admin/org/${org.org_id}`)}
                        >
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Low activation alert panel */}
      {pulseData.some(o => o.activationPct < 40 && o.total_users > 5) && (
        <Card className="border-yellow-500/30 bg-yellow-500/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
              <Users className="h-4 w-4" />
              Seats Not Activating
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pulseData
              .filter(o => o.activationPct < 40 && o.total_users > 5)
              .sort((a, b) => a.activationPct - b.activationPct)
              .map(o => (
                <div key={o.org_id} className="flex items-center justify-between text-sm">
                  <span className="font-medium">{o.org_name}</span>
                  <div className="flex items-center gap-3">
                    <div className="h-1.5 w-20 bg-muted rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500 rounded-full" style={{ width: `${o.activationPct}%` }} />
                    </div>
                    <span className="text-muted-foreground text-xs w-24 text-right">{o.active_users} of {o.total_users} active</span>
                    <Badge variant="outline" className="text-xs bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 border-yellow-500/30">
                      {o.activationPct}%
                    </Badge>
                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => navigate(`/admin/org/${o.org_id}`)}>
                      View →
                    </Button>
                  </div>
                </div>
              ))}
          </CardContent>
        </Card>
      )}

      {/* ── Health flags ──────────────────────────────────────────────── */}
      {flags.length > 0 ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Unresolved Health Flags
              <Badge variant="outline" className="text-xs ml-1">{flags.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            {/* High severity first */}
            {['high', 'medium', 'low'].map(sev => {
              const sevFlags = flags.filter(f => f.severity === sev);
              if (sevFlags.length === 0) return null;
              return (
                <div key={sev} className="mb-3 last:mb-0">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 px-1">
                    {sev} severity ({sevFlags.length})
                  </p>
                  {sevFlags.slice(0, sev === 'high' ? 999 : 5).map(f => (
                    <div key={f.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border/40 last:border-0">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="font-medium shrink-0">{flagOrgMap[f.org_id] ?? '—'}</span>
                        <span className="text-muted-foreground truncate">{f.flag_type.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0 ml-3">
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(f.created_at), { addSuffix: true })}
                        </span>
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => navigate(`/admin/org/${f.org_id}`)}>
                          View →
                        </Button>
                      </div>
                    </div>
                  ))}
                  {sev !== 'high' && sevFlags.length > 5 && (
                    <p className="text-xs text-muted-foreground text-center pt-1">+{sevFlags.length - 5} more</p>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      ) : (
        <Card className="border-green-500/20 bg-green-500/5">
          <CardContent className="py-6 text-center">
            <p className="text-sm text-green-700 dark:text-green-400 font-medium">No unresolved health flags</p>
            <p className="text-xs text-muted-foreground mt-1">All orgs are clear.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

